import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as Chat } from 'assets/icons/chat.svg';
import { useAppointmentCardHelper } from '../upcoming-appointments/helper/useAppointmentCardHelper';
import { AppointmentHasuraStatus } from '../../const/appointment';
import { ReactComponent as Download } from 'assets/icons/file-download.svg';
import {
  AppointmentTypeEnum,
  GetAppointmentOutput,
  GetFhirRecommendationOutput,
  GetPatientOutput,
  ProviderOutput,
  useGetEncounterSignUrlFromStorageMutation,
} from 'graphql/generated/hasura';
import { UpcomingAppoinment } from '../../interfaces';
import { useNavigate } from 'react-router-dom';
import { useGetGenericActions } from 'hooks/useGetGenericActions';
import { genericActionsIds, pageIds } from 'utilities/constants';
import { useGetPage } from 'hooks/useGetPage';
import {
  useGetAppointmentStatuses,
  useGetInfoAppointment,
} from './useGetInfoAppointment';
import ButtonComponent from 'components/button/buttonComponent';
import CustomTooltip from 'components/CustomTooltip';
import { CalendarDropdown } from 'components/dropdown/AddToCalendarDropdown';
import { mapGetAppointmentOutputToCalendarParams } from 'utilities/mapAppointmentToCalendarParams';
import Loader from 'components/loaderComponent';
import AppointmentInfoProvider from 'components/appointmentDetailModal/components/AppointmentInfoProvider';
import { Card } from '../Card';
import PreWorkSection from 'components/appointmentDetailModal/components/PreworkSection';
import CancelAppointmentModal from '../modalContent/CancelAppointmentModal';
import { MediaChatComponent } from '../MediaChatComponent';
import { Medications, MedicationsVeradigm } from '../medication/Medications';
import PastTestKits from '../complete-my-visit-summary/PastTestKits';
import TestKits from '../complete-my-visit-summary/TestKits';
import RecommendedProducts from '../recommended-products/details/RecommendedProducts';
import RecommendedProductsEdit from '../recommended-products/complete/RecommendedProducts';

import { PreviousAppointmentSummary } from '../PreviousAppointmentSummary';
import SummaryViewState from './SummaryViewState';
import SummaryEditState from './SummaryEditState';
import { FormProvider } from 'react-hook-form';
import { GetAppointmentStatuses, useSummaryForm } from './useSummariForm';
import { useRecommendedProducts } from '../recommended-products/complete/useRecommendedProducts';
import { MY_APPOINTMENTS } from 'utilities/routes';
import {
  AppointmentPaymentOutput,
  useAppendEncounterNotesToFhirAppointmentMutation,
  useEnsureAppointmentSummaryPdfMutation,
  useGetInvoiceOrReceiptUrlLazyQuery,
  usePatchFhirEncounterMutation,
  useRecommendProductsMutation,
} from 'graphql/generated/remote-schema-hasura';
import { useFormErrors } from 'components/stepper/useStepperForm';
import TestKitSummary from '../complete-my-visit-summary/TestKitSummary';
import { twMerge } from 'tailwind-merge';
import ProviderSkip from './ProviderSkip';
import PatientSkip from './PatientSkip';
import SummaryComingSoon from './SummaryComingSoon';
import { ChatComponent } from '../ChatComponent';
import { useAuth } from 'auth/context/AuthContext';
import moment from 'moment';
import NavigateBlockModal from 'components/NavigateBlockModal';
import debounce from 'lodash/debounce';
import { useSentryReplay } from 'hooks/useSentryReplay';
import Questionnaire from '../complete-my-visit-summary/Questionaire';
import BackButton from 'components/button/BackButton';
import AnotherProviderViewState from './AnotherProviderViewState';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';

export const isSummaryForm = 'isSummaryForm';
const POLLING_INTERVAL = 3_000;
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const AppointmentDetails = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { onError } = useFormErrors();
  const { startRecording, stopRecording } = useSentryReplay();
  const { downloadFileFromUrl } = useGetFileFromStorage();

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const afterSubmit = (isSaveAsDraft = false) => {
    navigate(MY_APPOINTMENTS, {
      state: {
        completedSummaryPatient: !isSaveAsDraft
          ? `${appointment?.patientFirstName} ${appointment?.patientLastName}`
          : null,
      },
    });
  };

  const [
    appendEncounterNotesToFhirAppointmentMutation,
    { loading: encounterAppointmentLoading },
  ] = useAppendEncounterNotesToFhirAppointmentMutation({
    refetchQueries: ['GetAppointmentByCodexAppointmentId'],
    onCompleted: () => afterSubmit(false),
  });

  const [patchFhirEncounterMutation, { loading: patchAppointmentLoading }] =
    usePatchFhirEncounterMutation({
      refetchQueries: ['GetAppointmentByCodexAppointmentId'],
    });

  const [recommendProducts, { loading: loadRecommend }] =
    useRecommendProductsMutation();

  const [getReceiptUrl] = useGetInvoiceOrReceiptUrlLazyQuery({
    onCompleted: (data) => {
      const receiptUrl = data?.getInvoiceOrReceiptUrl.receiptUrl;
      if (!receiptUrl) return;

      window.open(receiptUrl, '_blank');
    },
  });

  const handleClickDownloadReceipt: React.MouseEventHandler<
    HTMLButtonElement
  > = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    getReceiptUrl({
      variables: {
        appointmentId: appointmentId,
      },
    });
  };

  const { data: genericAction, loading: genericActionLoading } =
    useGetGenericActions({
      locale: 'en',
      genericActionId: [
        genericActionsIds.CANCEL,
        genericActionsIds.SUBMIT,
        genericActionsIds.CLOSE,
        genericActionsIds.BACK,
      ],
    });
  const { data: locale, loading: localeLoading } = useGetPage({
    locale: 'en',
    pageId: pageIds.APPOINTMENT_DETAILS,
  });

  const {
    appointment,
    appointmentId,
    provider,
    patient,
    appointmentLoading,
    patientLoading,
    providerDataLoading,

    chatMessages,
    chatMediaLoading,
    messageLoading,
    patientFullName,
    onMediaLocationClick,
  } = useGetInfoAppointment();

  const [getAppointmentSummary] = useEnsureAppointmentSummaryPdfMutation({
    fetchPolicy: 'network-only',
    onCompleted: (data) => console.log(data),
  });

  const [getEncounterUrl] = useGetEncounterSignUrlFromStorageMutation();

  const pollForSummaryPdf = async (
    appointmentId: string,
    signal?: AbortSignal,
  ): Promise<string> => {
    while (!signal?.aborted) {
      const { data, errors } = await getAppointmentSummary({
        variables: { appointmentCodexId: appointmentId },
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }

      const fileId = data?.ensureAppointmentSummaryPdf?.summaryPdfFileId;

      if (fileId) {
        return fileId;
      }

      await delay(POLLING_INTERVAL);
    }

    throw new Error('Polling aborted');
  };

  const handleDownloadClick = async () => {
    try {
      setIsLoading(true);
      const fileId =
        appointment?.summaryPdfFileId ||
        (await pollForSummaryPdf(appointmentId));
      const { data } = await getEncounterUrl({
        variables: {
          fileId,
          encounterId: appointment?.encounterId as string,
        },
      });
      if (data) {
        return downloadFileFromUrl(
          data?.GetEncounterSignUrlFromStorage?.url as string,
          appointmentId,
        );
      }
      return undefined;
    } catch (error) {
      console.error('Ошибка при получении PDF:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const {
    isProviderMissed,
    missed,
    isSubmittedProvider,
    isCanRescheduled,
    isProvider,
    isPatientMissed,
    isCompleted,
    hasSummary,
    hasntSummary,
  } = useGetAppointmentStatuses(appointment as GetAppointmentStatuses);

  const defaultProducts = useMemo(
    () =>
      (appointment?.recommendations || []).reduce(
        (acc, { productId, quantity }) => ({ ...acc, [productId]: quantity }),
        {},
      ),
    [appointment],
  );

  const {
    selectedProducts,
    products,
    updateSelectedProducts,
    handleSaveProduct,
    handleCancelSaveProduct,
    handleRecommendProduct,
    saveProducts,
    handleDelete,
    removeDraftRecommendation,
  } = useRecommendedProducts(defaultProducts);

  const {
    methods,
    handleDeleteFile,
    fileLoading,
    isFileLimitOver,
    fileError,
    handleImageUpload,
    mapFilesForApi,
    hasPolingLoadData,
  } = useSummaryForm(appointment as GetAppointmentStatuses);

  const {
    isNotPaid,
    hasPastTimeCancelAppointment,
    isJoinable,
    showReceipt,
    handleRetryPayment,
    handleClickCancel,
    handleClickReschedule,
    handleJoinAppointment,
    hasPastTimeRescheduleAppointment,
    loading: loadingRetryPaymentLink,
    isAddToCalendar,
  } = useAppointmentCardHelper({
    date: appointment?.start as string,
    appointmentStatus:
      appointment?.appointmentStatus as AppointmentHasuraStatus,
    end: appointment?.end as string,
    appointmentId,
    appointmentType: appointment?.appointmentType as AppointmentTypeEnum,
    patientId: appointment?.codexPatientId as string,
    providerId: appointment?.codexProviderId as string,
    paymentInformation: appointment?.paymentInformation,
  } as UpcomingAppoinment);

  const autosave = async () => {
    const {
      appointmentNotes,
      followupRecommendations,
      files,
      diagnosticReports,
      aiNotes,
      summaryDiagnosis,
      appointmentTranscriptions,
    } = methods.getValues();

    patchFhirEncounterMutation({
      variables: {
        encounterCodexId: appointment?.encounterId ?? '',
        providerNotes: appointmentNotes ?? '',
        followupRecommendations: followupRecommendations,
        providerSummary: summaryDiagnosis,
        files: (files ?? []).length ? mapFilesForApi(files || [[]]) : undefined,
        systemGenerated: aiNotes,
        transcription: appointmentTranscriptions,
        diagnosticReportIds: (diagnosticReports || [])
          .filter(({ isCheckbox }) => isCheckbox)
          .map(({ diagnosticReportId }) => diagnosticReportId),
      },
      onError: () => {
        setIsSavingDraft(false);
      },
    });
  };

  const saveForLater = async (callback?: () => void) => {
    if (appointment?.recommendations?.length) {
      await Promise.all(
        appointment?.recommendations?.map(({ id }) =>
          removeDraftRecommendation(id),
        ),
      );
    }

    if (saveProducts.length) {
      await recommendProducts({
        variables: {
          codexPatientId: appointment?.codexPatientId || '',
          appointmentCodexId: appointmentId,
          recommendations: saveProducts.map(({ id, amount }) => ({
            productCodexId: id,
            quantity: amount,
          })),
        },
      });
    }

    autosave()
      .then(() => callback?.())
      .catch(console.error);
  };

  const handleSubmit = methods.handleSubmit(
    async ({
      appointmentNotes,
      followupRecommendations,
      files,
      diagnosticReports,
      aiNotes,
      summaryDiagnosis,
      appointmentTranscriptions,
    }) => {
      if (appointment?.recommendations?.length) {
        await Promise.all(
          appointment?.recommendations?.map(({ id }) =>
            removeDraftRecommendation(id),
          ),
        );
      }

      if (saveProducts.length) {
        await recommendProducts({
          variables: {
            codexPatientId: appointment?.codexPatientId || '',
            appointmentCodexId: appointmentId,
            recommendations: saveProducts.map(({ id, amount }) => ({
              productCodexId: id,
              quantity: amount,
            })),
          },
        });
      }

      appendEncounterNotesToFhirAppointmentMutation({
        variables: {
          codexAppointmentId: appointment?.appointmentCodexId ?? '',
          providerNotes: appointmentNotes ?? '',
          followupRecommendations: followupRecommendations,
          providerSummary: summaryDiagnosis,
          files: (files ?? []).length
            ? mapFilesForApi(files || [[]])
            : undefined,
          systemGenerated: aiNotes,
          transcription: appointmentTranscriptions,
          diagnosticReportIds: (diagnosticReports || [])
            .filter(({ isCheckbox }) => isCheckbox)
            .map(({ diagnosticReportId }) => diagnosticReportId),
        },
      });
    },
    onError,
  );

  const debouncedAutosave = debounce(() => {
    if (methods.formState.isDirty) {
      autosave();
    }
  }, 5_000);

  useEffect(() => {
    const subscription = methods.watch(() => {
      debouncedAutosave();
    });

    return () => {
      subscription.unsubscribe();
      debouncedAutosave.cancel();
    };
  }, [methods, debouncedAutosave]);

  useEffect(() => {
    const initSentryReplay = async () => {
      startRecording();
    };
    if (isSubmittedProvider && hasntSummary) {
      initSentryReplay();
    }

    return () => {
      stopRecording();
    };
  }, [hasntSummary, isSubmittedProvider, startRecording, stopRecording]);

  if (
    localeLoading ||
    genericActionLoading ||
    !locale ||
    !genericAction ||
    !appointmentId
  )
    return null;

  return (
    <>
      <div className="flex justify-start w-16 -mb-5">
        <BackButton />
      </div>
      <div className="flex flex-col desktop:flex-row justify-between">
        <p
          className="text-h2 text-dark-gray font-medium desktop:text-h1 mb-2.5 desktop:mb-0"
          data-testid={'appointment-legal-doc-edit-page'}
        >
          {locale?.appointmentDetails}
        </p>

        <div className="flex flex-col desktop:flex-row items-center gap-2.5">
          {hasSummary && (
            <ButtonComponent
              type="underline"
              Icon={Download}
              testID="download-summary"
              onClick={handleDownloadClick}
              disabled={isLoading}
              showSpinner
            >
              Download summary
            </ButtonComponent>
          )}
          {isJoinable && (
            <>
              <ButtonComponent
                desktopFullWidth
                testID={`reschedule-${appointmentId}`}
                className={'whitespace-nowrap'}
                onClick={handleJoinAppointment}
              >
                {locale.joinNow || 'Join now'}
              </ButtonComponent>
            </>
          )}
          {!hasPastTimeCancelAppointment && (
            <>
              <ButtonComponent
                type="outlined"
                desktopFullWidth
                testID={`reschedule-${appointmentId}`}
                className={twMerge('whitespace-nowrap', isProvider && 'hidden')}
                dataTooltipId={
                  hasPastTimeRescheduleAppointment ? 'reschedule' : undefined
                }
                onClick={handleClickReschedule}
                disabled={hasPastTimeRescheduleAppointment}
              >
                {locale.rescheduleAppointment}
              </ButtonComponent>

              {isCanRescheduled && (
                <ButtonComponent
                  type="outlined"
                  testID={`reschedule-${appointmentId}`}
                  className={'whitespace-nowrap'}
                  onClick={handleClickReschedule}
                  // disabled={!!appointment?.pendingRescheduleRequest?.isActive}
                >
                  Request new time
                </ButtonComponent>
              )}

              <CustomTooltip
                className="max-w-[230px] text-left"
                place="top"
                id="reschedule"
                content="Rescheduling isn't available 24 hours before the appointment start time"
              />

              <ButtonComponent
                type="outlined"
                desktopFullWidth
                testID={`cancel-${appointmentId}`}
                className="whitespace-nowrap"
                onClick={handleClickCancel}
              >
                {locale.cancelAppointment}
              </ButtonComponent>
            </>
          )}
          {isNotPaid && (
            <ButtonComponent
              className="!text-sm rounded !px-3 ml-auto block"
              desktopFullWidth
              disabled={loadingRetryPaymentLink}
              testID={'retry-payment'}
              onClick={handleRetryPayment}
            >
              Retry payment
            </ButtonComponent>
          )}
          <ButtonComponent
            testID="view_receipt"
            Icon={Download}
            type="outlined"
            iconPosition="left"
            className={twMerge(
              'w-fit !px-5 !text-sm whitespace-nowrap max-desktop:w-full',
              !showReceipt && 'hidden',
            )}
            onClick={handleClickDownloadReceipt}
          >
            Download receipt
          </ButtonComponent>
          {isAddToCalendar && (
            <CalendarDropdown
              appointmentId={appointmentId}
              calendarEvent={mapGetAppointmentOutputToCalendarParams(
                appointment as GetAppointmentStatuses,
                isSubmittedProvider,
              )}
            />
          )}
        </div>
      </div>

      {appointmentLoading ||
      !appointment ||
      providerDataLoading ||
      patientLoading ? (
        <Loader />
      ) : (
        <div className="container flex flex-col desktop:flex-row gap-30 mx-auto">
          {patient && provider && (
            <div className="flex text-btn-black flex-col w-full gap-4 desktop:w-[300px]">
              <div className="flex flex-col w-full gap-4 desktop:w-[300px]">
                <AppointmentInfoProvider
                  datetime={appointment.start as string}
                  name={patientFullName}
                  appointmentType={appointment.appointmentType}
                  pictureOwnerCodexId={appointment.codexPatientId}
                  locale={locale}
                  profilePictureUrl={patient.SENSITIVE_profile_picture_id}
                  patient={patient as GetPatientOutput}
                  provider={provider as ProviderOutput}
                  appointmentPrice={appointment.paymentInformation?.amount}
                  discount={
                    (appointment.paymentInformation as AppointmentPaymentOutput)
                      ?.discount
                  }
                  duration={moment(appointment?.end).diff(
                    appointment?.start,
                    'minutes',
                  )}
                  appointmentStatus={
                    appointment.appointmentStatus as AppointmentHasuraStatus
                  }
                  statusHistory={appointment.statusHistory}
                />
              </div>
              <div
                className={twMerge(
                  'max-w-full overflow-y-auto max-h-[200px] desktop:max-h-[500px]',
                  !isCompleted && 'hidden',
                )}
              >
                <Card
                  title={'Attached files'}
                  titleClassName="flex text-h4 text-btn-black font-semibold"
                >
                  {chatMediaLoading ? (
                    <Loader />
                  ) : (
                    <MediaChatComponent
                      messages={chatMessages}
                      appointmentId={appointmentId}
                      loading={messageLoading}
                    />
                  )}
                </Card>
              </div>
            </div>
          )}

          <div className="w-full flex flex-col gap-2.5">
            <FormProvider {...methods}>
              <Card
                title={locale.appointmentPrework}
                titleClassName="flex text-h4 text-btn-black font-semibold"
                showSeparator
                defaultExpanded={!isCompleted}
                testId={'appointment-prework'}
              >
                <PreWorkSection
                  onMediaLocationClick={onMediaLocationClick}
                  appointmentData={appointment}
                  locale={locale}
                />
              </Card>

              {isSubmittedProvider && hasntSummary && (
                <>
                  <SummaryEditState
                    handleImageUpload={handleImageUpload}
                    handleDeleteFile={handleDeleteFile}
                    isFileLimitOver={isFileLimitOver}
                    fileLoading={fileLoading}
                    fileError={fileError}
                    appointment={appointment as GetAppointmentOutput}
                    hasPolingLoadData={hasPolingLoadData}
                  />
                  <RecommendedProductsEdit
                    products={products}
                    saveProducts={saveProducts}
                    selectedProducts={selectedProducts}
                    updateSelectedProducts={updateSelectedProducts}
                    handleSaveProduct={handleSaveProduct}
                    handleCancelSaveProduct={handleCancelSaveProduct}
                    handleRecommendProduct={handleRecommendProduct}
                    patientFullName={patientFullName}
                    handleDelete={handleDelete}
                  />
                  <TestKitSummary patientId={appointment?.codexPatientId} />

                  <NavigateBlockModal
                    isBlock={
                      !methods.formState.isSubmitSuccessful &&
                      methods.formState.isDirty &&
                      !isSavingDraft
                    }
                    leaveCallback={() => saveForLater()}
                    text={
                      'You are trying to leave before submitting the post-appointment summary. \nPlease hit the "submit" button at the bottom of the page in order to save the summary. If the write ups are not submitted, they will be discarded'
                    }
                  />
                </>
              )}

              {hasSummary && (
                <>
                  <SummaryViewState
                    appointment={appointment as GetAppointmentStatuses}
                  />
                  <RecommendedProducts
                    recommendations={
                      (appointment?.recommendations ??
                        []) as GetFhirRecommendationOutput[]
                    }
                    providerId={appointment?.codexProviderId as string}
                  />
                </>
              )}

              {isProviderMissed && (
                <ProviderSkip
                  lastUpdated={appointment?.encounterNotes?.lastUpdated}
                />
              )}
              {(isPatientMissed || missed) && (
                <PatientSkip
                  lastUpdated={appointment?.encounterNotes?.lastUpdated}
                />
              )}
              {hasntSummary && !isProvider && (
                <SummaryComingSoon
                  lastUpdated={appointment?.encounterNotes?.lastUpdated}
                />
              )}

              {hasntSummary && isProvider && !isSubmittedProvider && (
                <AnotherProviderViewState />
              )}

              {!(isProvider && hasntSummary) &&
                !missed &&
                !isPatientMissed &&
                !isProviderMissed && (
                  <TestKits
                    patientId={
                      isProvider ? appointment?.codexPatientId : undefined
                    }
                    diagnosticReports={appointment?.diagnosticReports ?? []}
                    showQuestionnaireButton
                    showResultButton={isSubmittedProvider || isCompleted}
                  />
                )}
            </FormProvider>

            <MedicationsVeradigm
              encounterId={appointment.encounterId ?? ''}
              appointmentCodexId={appointmentId}
              patientCodexId={appointment?.codexPatientId}
              providerCodexId={appointment?.codexProviderId as string}
              title="Prescribed medications"
              isEditMode={hasntSummary && isSubmittedProvider}
            />

            <Medications
              encounterId={appointment.encounterId ?? ''}
              appointmentCodexId={appointmentId}
              patientCodexId={appointment?.codexPatientId}
              title="Recommend supplements"
              isEditMode={hasntSummary && isSubmittedProvider}
            />

            {!missed && !isPatientMissed && !isProviderMissed && (
              <PastTestKits
                patientId={appointment?.codexPatientId}
                appointmentId={appointmentId}
              />
            )}

            {!missed && !isPatientMissed && !isProviderMissed && (
              <PreviousAppointmentSummary
                patientCodexId={appointment?.codexPatientId}
              />
            )}

            {!missed && !isPatientMissed && !isProviderMissed && (
              <Questionnaire patientId={appointment?.codexPatientId} />
            )}

            {isCompleted && (
              <Card
                title={'Chat log'}
                titleClassName="flex text-h4 text-btn-black font-semibold ml-3"
                Icon={() => <Chat className="w-5 h-5 fill-neutral-800" />}
                backgroundColor="bg-white"
                showSeparator
              >
                {chatMediaLoading ? (
                  <Loader />
                ) : (
                  <ChatComponent
                    loggedUser={user}
                    patientId={appointment?.codexPatientId}
                    providerId={appointment?.codexProviderId ?? ''}
                    appointmentId={appointmentId}
                    locale={locale?.chatMessage}
                    messages={chatMessages}
                  />
                )}
              </Card>
            )}

            <div
              className={twMerge(
                'flex items-center justify-between px-5 pt-5 pb-30 desktop:p-30 gap-5 bg-white rounded-10',
                (!hasntSummary || !isSubmittedProvider) && 'hidden',
              )}
            >
              <ButtonComponent
                onClick={() => {
                  setIsSavingDraft(true);
                  saveForLater(() => afterSubmit(true));
                }}
                disabled={patchAppointmentLoading || loadRecommend}
                showSpinner
                type={'underline'}
                testID={'save-for-later'}
              >
                {genericAction?.[genericActionsIds.SAVE_FOR_LATER]
                  ?.saveForLater ?? 'Save for later'}
              </ButtonComponent>

              <ButtonComponent
                onClick={handleSubmit}
                disabled={encounterAppointmentLoading || loadRecommend}
                showSpinner
                testID={'submit'}
              >
                {genericAction?.[genericActionsIds.SUBMIT]?.submit}
              </ButtonComponent>
            </div>
          </div>
        </div>
      )}
      <CancelAppointmentModal />
    </>
  );
};

export default AppointmentDetails;
